import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { apiJson } from '../apiClient';
import { normalizeRole } from '../utils/userLocale';
import { hasTenantModule } from '../utils/tenantModules';
import './SettingsPages.css';

const DEFAULT_NOTIFY = {
  lowStockInApp: true,
  lowStockSms: false,
  orderApprovalsInApp: true,
  subscriptionReminders: true,
  wakalaSmsAlerts: true,
  dailySummarySms: false,
};

const OWNER_TOGGLES = [
  {
    key: 'lowStockInApp',
    label: 'Low stock (in-app)',
    desc: 'Bell icon at the top — alerts when products run low.',
    bell: true,
  },
  {
    key: 'lowStockSms',
    label: 'Low stock (SMS)',
    desc: 'Text message to your business phone when stock is critical.',
  },
  {
    key: 'orderApprovalsInApp',
    label: 'Orders & approvals',
    desc: 'New orders and items waiting for owner approval.',
    bell: true,
  },
  {
    key: 'subscriptionReminders',
    label: 'Subscription reminders',
    desc: 'Trial ending and renewal notices before access stops.',
    bell: true,
  },
  {
    key: 'wakalaSmsAlerts',
    label: 'Wakala / mobile money SMS',
    desc: 'Inbound SMS parsing for agent cashbook (when Wakala module is on).',
    module: 'cashbook',
  },
  {
    key: 'dailySummarySms',
    label: 'Daily summary SMS',
    desc: 'End-of-day sales summary to business phone (coming soon).',
    soon: true,
  },
];

const STAFF_TOGGLES = [
  {
    key: 'lowStockInApp',
    label: 'Low stock alerts',
    desc: 'Shown in the notification bell at the top.',
    bell: true,
  },
  {
    key: 'orderApprovalsInApp',
    label: 'Order updates',
    desc: 'Status changes on orders you follow.',
    bell: true,
  },
];

function SettingsNotificationsPage({ onNavigate }) {
  const { user, refreshMe } = useAuth();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const [prefs, setPrefs] = useState({ ...DEFAULT_NOTIFY });
  const [businessPhone, setBusinessPhone] = useState('');
  const [loading, setLoading] = useState(isOwner);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const toggles = (isOwner ? OWNER_TOGGLES : STAFF_TOGGLES).filter((t) => {
    if (t.module && !hasTenantModule(user, t.module)) return false;
    return true;
  });

  const load = useCallback(async () => {
    if (!isOwner) {
      const p = user?.preferences?.notifications || {};
      setPrefs({ ...DEFAULT_NOTIFY, ...p });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await apiJson('/owner/tenant-settings');
      const t = data.tenant || {};
      setBusinessPhone(t.phone || '');
      setPrefs({ ...DEFAULT_NOTIFY, ...(t.notifications || {}) });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Could not load notification settings.' });
    } finally {
      setLoading(false);
    }
  }, [isOwner, user]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (key) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  const handleSave = async () => {
    setBusy(true);
    setMsg(null);
    try {
      if (isOwner) {
        await apiJson('/owner/tenant-settings', {
          method: 'PATCH',
          body: JSON.stringify({ notifications: prefs }),
        });
      } else {
        await apiJson('/me/preferences', {
          method: 'PATCH',
          body: JSON.stringify({ notifications: prefs }),
        });
      }
      await refreshMe();
      setMsg({ type: 'success', text: 'Notification preferences saved.' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-bleed settings-page">
      <button type="button" className="settings-back" onClick={() => onNavigate('settings')}>
        ← Settings
      </button>
      <h1 className="settings-page__title">Notifications</h1>
      <p className="settings-page__lead">Choose what appears in the bell or by SMS.</p>

      {isOwner && businessPhone && (
        <p className="settings-page__hint">
          SMS alerts use business phone: <strong>{businessPhone}</strong> — change it under Business profile.
        </p>
      )}

      {msg && <div className={`settings-msg settings-msg--${msg.type}`}>{msg.text}</div>}

      {loading ? (
        <p className="settings-loading">Loading…</p>
      ) : (
        <>
          <ul className="settings-module-list">
            {toggles.map((t) => (
              <li key={t.key} className={`settings-module-row${prefs[t.key] ? ' is-on' : ''}`}>
                <div className="settings-module-row__text">
                  <strong>
                    {t.label}
                    {t.bell ? <span className="settings-inline-badge">Top bar</span> : null}
                    {t.soon ? <span className="settings-inline-badge settings-inline-badge--muted">Soon</span> : null}
                  </strong>
                  <span>{t.desc}</span>
                </div>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!prefs[t.key]}
                    disabled={busy || t.soon}
                    onChange={() => toggle(t.key)}
                  />
                  <span className="settings-toggle__track" aria-hidden />
                </label>
              </li>
            ))}
          </ul>
          <button type="button" className="settings-save" disabled={busy} onClick={handleSave}>
            {busy ? 'Saving…' : 'Save notification settings'}
          </button>
        </>
      )}
    </div>
  );
}

export default SettingsNotificationsPage;
